import { create } from '@bufbuild/protobuf'

import type {
  ExcludeArea as ProcessImageExcludeArea,
  MainAreaColor,
  MainAreaMaterial,
  ProcessImageParameters,
  SubAreaColor,
} from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import {
  ExcludeAreaSchema,
  MainAreaColorSchema,
  MainAreaMaterialSchema,
  ProcessImageParametersSchema,
  SubAreaColorSchema,
} from '@ai-color-modules/gen_proto/ai_color_simulation/v1/messages_pb'
import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import type {
  ExcludeArea as SimulationExcludeArea,
  MergedPolygonsByArea,
  Point as SimulationPoint,
  SubArea as SimulationSubArea,
} from '@ai-color-modules/simulation/types'

export const BASE_AREAS = ['外壁', '屋根', '雨樋', '破風・鼻隠し']

// 日本語ベースエリア名 → canonical 英語名（API / preset default のキー体系）
export const AREA_NAME_MAP: Record<string, string> = {
  外壁: 'outer wall and balcony',
  屋根: 'roof, ridge',
  雨樋: 'gutter',
  '破風・鼻隠し': 'fascia, barge',
}

// 英語API用名から日本語エリア名へのマッピング（逆変換）
export const API_TO_AREA_NAME_MAP: Record<string, string> = {
  'outer wall and balcony': '外壁',
  'roof, ridge': '屋根',
  gutter: '雨樋',
  'fascia, barge': '破風・鼻隠し',
}

const toApiAreaName = (areaLabel: string) => AREA_NAME_MAP[areaLabel] ?? areaLabel

/**
 * ベースにした画像の Parameters から、指定エリア・色コードに一致する RGB を引く.
 *
 * 履歴色（プリセット外の過去使用色）は availableColors から消えたため、そのままだと
 * r/g/b=0（黒）に落ちてしまう. 再シミュレーション時に元画像 Parameters の RGB を
 * 引き継ぐことで、候補に無い色でも正しい RGB でリクエストできるようにする.
 * area / code の両方が一致するものを優先し、無ければ code だけ一致（sub area 含む）にフォールバックする.
 */
const findRgbFromBaseParameters = (
  baseParameters: ProcessImageParameters | null | undefined,
  apiAreaName: string,
  colorValue: string | undefined,
): { r: number; g: number; b: number } | undefined => {
  if (!baseParameters || !colorValue) return undefined

  const mainMatch = (baseParameters.mainAreas ?? []).find(
    area => area.areaName === apiAreaName && area.color === colorValue,
  )
  if (mainMatch) return { r: mainMatch.r, g: mainMatch.g, b: mainMatch.b }

  const anyMainMatch = (baseParameters.mainAreas ?? []).find(area => area.color === colorValue)
  if (anyMainMatch) return { r: anyMainMatch.r, g: anyMainMatch.g, b: anyMainMatch.b }

  const subMatch = (baseParameters.subAreas ?? []).find(area => area.color === colorValue)
  if (subMatch) return { r: subMatch.r, g: subMatch.g, b: subMatch.b }

  return undefined
}

// 統合ポリゴン（issue #2224）を proto Polygon[] へ変換する。
// 3 頂点未満は無効なので落とす。座標は round してオリジナル画像座標系で送る。
const toMergedPolygonsPayload = (polygons: SimulationPoint[][] | undefined) =>
  (polygons ?? [])
    .filter(poly => poly.length >= 3)
    .map(poly => ({
      points: poly.map(point => ({
        x: Math.round(point.originalX),
        y: Math.round(point.originalY),
      })),
    }))

const createMainAreaColor = (
  areaLabel: string,
  colorValue: string | undefined,
  availableColors: ColorOption[],
  baseParameters?: ProcessImageParameters | null,
  mergedPolygons?: SimulationPoint[][],
) => {
  const apiAreaName = toApiAreaName(areaLabel)
  const colorOption = availableColors.find(option => option.value === colorValue)
  // カタログに無い履歴色は、ベース画像 Parameters の RGB を引き継ぐ（miss 時のみ 0,0,0）.
  const fallbackRgb = colorOption
    ? undefined
    : findRgbFromBaseParameters(baseParameters, apiAreaName, colorValue)

  return create(MainAreaColorSchema, {
    areaName: apiAreaName,
    color: colorValue ?? '',
    r: colorOption?.r ?? fallbackRgb?.r ?? 0,
    g: colorOption?.g ?? fallbackRgb?.g ?? 0,
    b: colorOption?.b ?? fallbackRgb?.b ?? 0,
    // 統合されたカスタムエリアのポリゴン領域（issue #2224）。空なら未設定（後方互換）。
    mergedPolygons: toMergedPolygonsPayload(mergedPolygons),
  })
}

/** 統合ポリゴン（issue #2224）を 1 つ以上持つベースエリアがあるか */
export const hasAnyMergedPolygons = (mergedPolygonsByArea: MergedPolygonsByArea): boolean =>
  BASE_AREAS.some(areaLabel =>
    (mergedPolygonsByArea[areaLabel] ?? []).some(poly => poly.length >= 3),
  )

export const buildMainAreaColors = (
  areaColors: Record<string, string>,
  hasValidSubAreas: boolean,
  availableColors: ColorOption[],
  hasActiveMaterials = false,
  baseParameters?: ProcessImageParameters | null,
  // ベースエリア（日本語ラベル）→ 統合ポリゴン群（issue #2224）
  mergedPolygonsByArea: MergedPolygonsByArea = {},
): MainAreaColor[] => {
  const hasColoredMainAreas = BASE_AREAS.some(areaLabel => Boolean(areaColors[areaLabel]))
  const hasMergedPolygons = hasAnyMergedPolygons(mergedPolygonsByArea)

  // 統合ポリゴンだけがあるケースでも MainAreaColor を emit する（色は空、Python 側は union のみ実施）。
  if (hasColoredMainAreas || hasValidSubAreas || hasActiveMaterials || hasMergedPolygons) {
    const baseAreaColors = BASE_AREAS.map(areaLabel =>
      createMainAreaColor(
        areaLabel,
        areaColors[areaLabel],
        availableColors,
        baseParameters,
        mergedPolygonsByArea[areaLabel],
      ),
    )

    const extraAreaColors = Object.entries(areaColors)
      .filter(([areaLabel, color]) => !BASE_AREAS.includes(areaLabel) && Boolean(color))
      .map(([areaLabel, colorValue]) =>
        createMainAreaColor(areaLabel, colorValue, availableColors, baseParameters),
      )

    return [...baseAreaColors, ...extraAreaColors]
  }

  return []
}

/**
 * 保存済み ProcessImageParameters から、ベースエリア（日本語ラベル）→ 統合ポリゴン群を復元する（issue #2224）。
 * 再シミュレーション時・共有リンク復元時に編集ステートへ戻すために使う。
 */
export const extractMergedPolygonsByArea = (
  parameters: ProcessImageParameters | null | undefined,
): MergedPolygonsByArea => {
  const result: MergedPolygonsByArea = {}
  for (const mainArea of parameters?.mainAreas ?? []) {
    const areaLabel = API_TO_AREA_NAME_MAP[mainArea.areaName]
    if (!areaLabel) continue
    const polygons = (mainArea.mergedPolygons ?? [])
      .map(poly => (poly.points ?? []).map(point => ({ originalX: point.x, originalY: point.y })))
      .filter(poly => poly.length >= 3)
    if (polygons.length > 0) {
      result[areaLabel] = polygons
    }
  }
  return result
}

/** ベースエリアごとの「色 or 素材どちらが現在 active か」 */
export type AreaSelectionMode = 'color' | 'material'

/**
 * エリアごとの active mode を解決する.
 * 明示的に areaSelectionMode に値があればそれを優先。無ければ:
 * - 素材だけ set → 'material'
 * - 色だけ set → 'color'
 * - 両方 set or どちらも未設定 → undefined（どちらか判別できないので両方を出さない）
 */
const resolveActiveMode = (
  areaLabel: string,
  areaColors: Record<string, string>,
  areaMaterials: Record<string, bigint>,
  areaSelectionMode: Record<string, AreaSelectionMode>,
): AreaSelectionMode | undefined => {
  const explicit = areaSelectionMode[areaLabel]
  if (explicit) return explicit
  const hasColor = Boolean(areaColors[areaLabel])
  const hasMaterial = areaMaterials[areaLabel] !== undefined && areaMaterials[areaLabel] !== 0n
  if (hasMaterial && !hasColor) return 'material'
  if (hasColor && !hasMaterial) return 'color'
  return undefined
}

/**
 * 屋根・外壁の素材選択を MainAreaMaterial[] に変換する.
 * 素材が active mode のエリアのみが対象（色 active のエリアは含めない）.
 *
 * `materialImageS3Path` は Go 側で `material_id` から解決して埋めるため、ここでは空のままで良い.
 */
export const buildMainAreaMaterials = (
  areaMaterials: Record<string, bigint>,
  areaSelectionMode: Record<string, AreaSelectionMode> = {},
  areaColors: Record<string, string> = {},
  // API から取得済みの有効な素材ID集合。指定時、ここに無い ID のアサインは送らない。
  // （削除済みなどで一覧から消えた素材が状態に残っていても、UI に出ていないものを暗黙的に
  //   リクエストへ載せて 404「素材が見つかりません」になる事故を防ぐ。未指定なら従来通り無検証。）
  availableMaterialIds?: ReadonlySet<bigint>,
): MainAreaMaterial[] => {
  return Object.entries(areaMaterials)
    .filter(([areaLabel, materialId]) => {
      // 1st リリースは屋根・外壁ベースエリアのみ素材選択可能
      const isMaterialEligibleArea = areaLabel === '外壁' || areaLabel === '屋根'
      if (!isMaterialEligibleArea || materialId === 0n) return false
      // 取得済み素材一覧に存在しない ID は送らない（暗黙残存ガード）
      if (availableMaterialIds && !availableMaterialIds.has(materialId)) return false
      // active mode が material のエリアだけを採用
      const mode = resolveActiveMode(areaLabel, areaColors, areaMaterials, areaSelectionMode)
      return mode === 'material'
    })
    .map(([areaLabel, materialId]) =>
      create(MainAreaMaterialSchema, {
        areaName: toApiAreaName(areaLabel),
        materialId,
      }),
    )
}

interface BuildProcessImageParametersOptions {
  areaColors: Record<string, string>
  hasValidSubAreas: boolean
  subAreas: SimulationSubArea[]
  excludeAreas: SimulationExcludeArea[]
  unifyColor: boolean
  /** API から取得した色マスター。色コード → r/g/b 解決に使う */
  availableColors: ColorOption[]
  /** エリアラベル → 選択中の素材ID. 未選択は省略 or 0n. */
  areaMaterials?: Record<string, bigint>
  /** エリアラベル → 'color' | 'material' で active な側を表す */
  areaSelectionMode?: Record<string, AreaSelectionMode>
  /** API 取得済みの有効な素材ID集合。一覧に無い ID のアサインはリクエストに載せない。 */
  availableMaterialIds?: ReadonlySet<bigint>
  /**
   * ベースにした結果画像の Parameters（再シミュレーション時のみ）.
   * availableColors に無い履歴色コードの RGB を、この Parameters から引き継ぐ.
   */
  baseParameters?: ProcessImageParameters | null
  /** ベースエリア（日本語ラベル）→ 統合ポリゴン群（issue #2224） */
  mergedPolygonsByArea?: MergedPolygonsByArea
}

export const buildProcessImageParameters = ({
  areaColors,
  hasValidSubAreas,
  subAreas,
  excludeAreas,
  unifyColor,
  availableColors,
  areaMaterials,
  areaSelectionMode,
  availableMaterialIds,
  baseParameters,
  mergedPolygonsByArea,
}: BuildProcessImageParametersOptions) => {
  const materials = areaMaterials ?? {}
  const modes = areaSelectionMode ?? {}
  const mergedByArea = mergedPolygonsByArea ?? {}

  // active mode が 'material' のエリアは色変換から除外する.
  // mode が無いエリアは「素材だけ set 」 → material、「色だけ set 」 → color、両方ある場合は両方を除外.
  const filteredAreaColors = Object.fromEntries(
    Object.entries(areaColors).filter(([areaLabel]) => {
      const mode = resolveActiveMode(areaLabel, areaColors, materials, modes)
      // 'color' の場合は採用、'material' なら除外、undefined（両方 set で曖昧）も除外
      return mode === 'color'
    }),
  )

  const mainAreaMaterials = buildMainAreaMaterials(
    materials,
    modes,
    areaColors,
    availableMaterialIds,
  )
  // 素材だけが指定されているエリアでも Python 側のバリデーション
  // ("Requires at least one main_area for color specification") を満たすために、
  // 素材が有効な場合は 4ベースエリアを必ず空 color で同梱する.
  const mainAreas = buildMainAreaColors(
    filteredAreaColors,
    hasValidSubAreas,
    availableColors,
    mainAreaMaterials.length > 0,
    baseParameters,
    mergedByArea,
  )

  const processedSubAreas: SubAreaColor[] = subAreas
    .filter(
      area =>
        area.color && (area.points.length >= 3 || area.polygons?.some(poly => poly.length >= 3)),
    )
    .map(area => {
      const colorOption = availableColors.find(option => option.value === area.color)
      // カタログに無い履歴色は、ベース画像 Parameters の RGB を引き継ぐ（miss 時のみ 0,0,0）.
      const fallbackRgb = colorOption
        ? undefined
        : findRgbFromBaseParameters(baseParameters, '', area.color)
      const polygonPoints = area.points.map(point => ({
        x: Math.round(point.originalX),
        y: Math.round(point.originalY),
      }))
      const polygons =
        area.polygons
          ?.filter(poly => poly.length >= 3)
          .map(poly => ({
            points: poly.map(point => ({
              x: Math.round(point.originalX),
              y: Math.round(point.originalY),
            })),
          })) ?? []

      return create(SubAreaColorSchema, {
        color: area.color,
        r: colorOption?.r ?? fallbackRgb?.r ?? 0,
        g: colorOption?.g ?? fallbackRgb?.g ?? 0,
        b: colorOption?.b ?? fallbackRgb?.b ?? 0,
        polygon: polygonPoints.length > 0 ? { points: polygonPoints } : undefined,
        polygons,
        useDetectedAreaOnly: area.useDetectedAreaOnly ?? true,
        label: area.label ?? '',
      })
    })

  const processedExcludeAreas: ProcessImageExcludeArea[] = excludeAreas
    .filter(area => area.points.length >= 3)
    .map(area =>
      create(ExcludeAreaSchema, {
        polygon: {
          points: area.points.map(point => ({
            x: Math.round(point.originalX),
            y: Math.round(point.originalY),
          })),
        },
      }),
    )

  return create(ProcessImageParametersSchema, {
    mainAreas,
    subAreas: processedSubAreas,
    excludeAreas: processedExcludeAreas,
    reprocess: false,
    unifyTwoToOne: unifyColor,
    mainAreaMaterials,
  })
}
